/**
 * cart-auto-add-gift.js
 * Automatically adds a product to the cart when the cart total is above a
 * threshold. Product and threshold are configured in Theme settings > Auto-add
 * gift and passed in via window.OlayAutoGift. When no product is selected, it
 * falls back to resolving a barcode/UPC via the predictive search API.
 *
 * Opt-out behaviour: once the gift has been added and the shopper manually
 * removes it, a flag is stored in localStorage and the script will never add
 * it again for that browser.
 */
(function () {
  'use strict';

  var SETTINGS = window.OlayAutoGift || {};

  var CONFIG = {
    enabled: SETTINGS.enabled !== false,                                  // default on
    productId: SETTINGS.productId ? parseInt(SETTINGS.productId, 10) : null, // from theme settings
    variantId: SETTINGS.variantId ? parseInt(SETTINGS.variantId, 10) : null, // from theme settings
    available: SETTINGS.available,                                        // variant availability
    barcode: SETTINGS.barcode || null,                                   // optional fallback UPC/GTIN
    thresholdCents: typeof SETTINGS.thresholdCents === 'number' ? SETTINGS.thresholdCents : null,
    quantity: 1
  };

  // Storage keys are scoped to the target product (variant id or barcode).
  var KEY = CONFIG.variantId ? 'v' + CONFIG.variantId : 'b' + CONFIG.barcode;
  var STORAGE = {
    removed: 'olay:autoGift:removed:' + KEY,        // '1' => never add again
    added: 'olay:autoGift:added:' + KEY,            // holds variant id while gift is in cart
    variantCache: 'olay:autoGift:variant:' + KEY,   // resolved variant id cache
    lock: 'olay:autoGift:lock:' + KEY               // cross-tab "add in progress" lock
  };
  var LOCK_TTL_MS = 8000; // stale-lock safety net (e.g. a crashed/closed tab)

  var CART_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart) || '/cart';

  var running = false;        // re-entrancy guard
  var pending = false;         // a cart-update arrived while running — rerun after
  var resolutionFailed = false; // stop retrying barcode resolution this page load

  /* ── localStorage helpers (fail-safe if storage is unavailable) ── */
  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) { /* ignore */ }
  }
  function lsRemove(key) {
    try { window.localStorage.removeItem(key); } catch (e) { /* ignore */ }
  }

  /* ── Cross-tab "add in progress" lock, so two tabs/triggers can't both add ── */
  function acquireLock() {
    var existing = lsGet(STORAGE.lock);
    if (existing && Date.now() - parseInt(existing, 10) < LOCK_TTL_MS) return false;
    lsSet(STORAGE.lock, String(Date.now()));
    return true;
  }
  function releaseLock() {
    lsRemove(STORAGE.lock);
  }

  /* ── Barcode comparison (tolerant of leading zeros) ── */
  function barcodeMatches(candidate) {
    if (!candidate) return false;
    var a = String(candidate).trim();
    var b = String(CONFIG.barcode).trim();
    if (a === b) return true;
    // Compare with leading zeros stripped so "00075..." matches "75..."
    return a.replace(/^0+/, '') === b.replace(/^0+/, '');
  }

  /* ── Resolve the target variant id (settings first, then barcode) ── */
  function resolveVariantId() {
    // Preferred: a real variant id chosen via the theme setting product picker.
    if (CONFIG.variantId) return Promise.resolve(CONFIG.variantId);

    var cached = lsGet(STORAGE.variantCache);
    if (cached) return Promise.resolve(parseInt(cached, 10));
    if (resolutionFailed) return Promise.resolve(null);
    if (!CONFIG.barcode) { resolutionFailed = true; return Promise.resolve(null); }

    var url = '/search/suggest.json'
      + '?q=' + encodeURIComponent(CONFIG.barcode)
      + '&resources[type]=product'
      + '&resources[options][unavailable_products]=last'
      + '&resources[options][fields]=variants.barcode'
      + '&resources[options][limit]=10';

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        if (!products.length) return null;
        // Fetch each candidate product's full data and match the variant barcode.
        return findVariantInProducts(products, 0);
      })
      .then(function (variantId) {
        if (variantId) {
          lsSet(STORAGE.variantCache, String(variantId));
        } else {
          resolutionFailed = true;
        }
        return variantId;
      })
      .catch(function () {
        resolutionFailed = true;
        return null;
      });
  }

  function findVariantInProducts(products, index) {
    if (index >= products.length) return null;
    var handle = products[index].handle;
    if (!handle) return findVariantInProducts(products, index + 1);

    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) {
        var variants = (product && product.variants) || [];
        for (var i = 0; i < variants.length; i++) {
          if (barcodeMatches(variants[i].barcode)) {
            return variants[i].id;
          }
        }
        return findVariantInProducts(products, index + 1);
      })
      .catch(function () {
        return findVariantInProducts(products, index + 1);
      });
  }

  /* ── Refresh the theme's cart UI after a direct cart mutation ── */
  function refreshCartUI() {
    if (typeof window.trigger === 'function') {
      window.trigger('theme:cart:refresh');
    } else {
      window.dispatchEvent(new Event('theme:cart:refresh'));
    }
    window.dispatchEvent(new CustomEvent('theme:cart:updated'));
  }

  /* ── Add the gift to the cart ── */
  function addGift(variantId) {
    // Preferred: use the theme's Cart custom element so the cart count and all
    // cart sections (drawer / cart page) are re-rendered in place — no reload.
    var CartEl = window.customElements && customElements.get('cart-element');
    if (CartEl && typeof CartEl.addItems === 'function') {
      var sections = CartEl.sections && CartEl.sections.length ? CartEl.sections : ['cart-drawer'];
      var opts = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: CONFIG.quantity }],
          sections: sections
        })
      };
      return CartEl.addItems(opts, false).then(function () {
        lsSet(STORAGE.added, String(variantId));
      });
    }

    // Fallback: add via AJAX and ask the theme to refresh the cart HTML.
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: CONFIG.quantity, sections: ['cart-drawer'] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function () {
        lsSet(STORAGE.added, String(variantId));
        refreshCartUI();
      });
  }

  /* ── Core decision logic ── */
  function maybeRun() {
    if (running) { pending = true; return; }

    running = true;

    var cart;
    fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        cart = data;
        if (!cart) return null;

        // Disabled in theme settings, or the shopper already opted out.
        if (!CONFIG.enabled) return null;
        if (lsGet(STORAGE.removed) === '1') return null;
        // Nothing to add, or no threshold configured.
        if (!CONFIG.variantId && !CONFIG.barcode) return null;
        if (typeof CONFIG.thresholdCents !== 'number') return null;
        if (CONFIG.available === false) return null;

        return resolveVariantId();
      })
      .then(function (variantId) {
        if (!cart || !variantId) return;

        // Treat the gift as already present when the SAME PRODUCT is in the
        // cart (any variant), so we never add a second line for it.
        var productPresent = (cart.items || []).some(function (item) {
          return CONFIG.productId
            ? item.product_id === CONFIG.productId
            : item.variant_id === variantId;
        });

        if (productPresent) {
          // Remember it's present so we can detect a manual removal.
          lsSet(STORAGE.added, String(variantId));
          return;
        }

        // Product is not in the cart.
        if (lsGet(STORAGE.added)) {
          // It was added before and is now gone => shopper removed it manually.
          lsSet(STORAGE.removed, '1');
          lsRemove(STORAGE.added);
          return;
        }

        // Never added yet — add it if the total is above the threshold.
        if (cart.total_price > CONFIG.thresholdCents) {
          // Final guard immediately before writing: acquire a cross-tab lock
          // and re-confirm the product still isn't present, in case another
          // tab/trigger added it in the time it took to get here.
          if (!acquireLock()) return;
          return fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (freshCart) {
              if (!freshCart) return;
              var stillAbsent = !(freshCart.items || []).some(function (item) {
                return CONFIG.productId
                  ? item.product_id === CONFIG.productId
                  : item.variant_id === variantId;
              });
              if (stillAbsent && freshCart.total_price > CONFIG.thresholdCents) {
                return addGift(variantId);
              }
            })
            .then(function (result) { releaseLock(); return result; })
            .catch(function (err) { releaseLock(); throw err; });
        }
      })
      .catch(function () { /* fail silently */ })
      .then(function () {
        running = false;
        if (pending) { pending = false; maybeRun(); }
      });
  }

  /* ── Wire up ── */
  var debounceTimer = null;
  function scheduleRun() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(maybeRun, 300);
  }

  window.addEventListener('theme:cart:updated', scheduleRun);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeRun);
  } else {
    maybeRun();
  }
})();
